import { createAsyncThunk, createSlice, nanoid, PayloadAction } from '@reduxjs/toolkit';

import {
  IUpdateSessionState,
  OfflineSessionPing,
  SessionId,
  SessionPayload,
  SessionPing,
} from './update-session.interfaces';

import type { RootState } from '../extension-store/extension.store';
import { StateStatus } from '../extension-store/interfaces/extension-store.interfaces';
import PubsubService, { PostSuccess } from '../pubsub/pubsub.service';
import UtilsSerice from '../utils/utils.service';

const initialState: IUpdateSessionState = {
  sessionPings: [],
  offlineSessionPings: [],
  error: null,
  status: StateStatus.IDLE,
};

export const UpdateSessionEffects = {
  sendSessionPings: createAsyncThunk(
    'UpdateSession/sendSessionPings',
    async (sessionPings: SessionPing[]): Promise<{ publishStatus: PostSuccess; sessions: SessionPing[] }> => ({
      publishStatus: await PubsubService.publishToTopic<SessionPayload>(sessionPings.map((sp) => sp.sessionPayload)),
      sessions: sessionPings,
    })
  ),
};

export const UpdateSessionState = createSlice({
  name: 'UpdateSession',
  initialState,
  reducers: {
    addOfflineSessionPing: {
      reducer(mutableState, action: PayloadAction<OfflineSessionPing>) {
        mutableState.offlineSessionPings.push(action.payload);
      },
      prepare(sessionPayload: Partial<SessionPayload>) {
        return {
          payload: {
            sessionPayload: { ...sessionPayload, timestamp: sessionPayload.timestamp ?? new Date().toISOString() },
            id: nanoid(),
          },
        };
      },
    },

    addHydratedSessionPing: (mutableState, action: PayloadAction<SessionPing>) => {
      mutableState.sessionPings.push(action.payload);
    },
    addSessionPing: {
      reducer(mutableState, action: PayloadAction<SessionPing>) {
        mutableState.sessionPings.push(action.payload);
      },
      prepare(sessionPayload: SessionPayload) {
        return {
          payload: {
            sessionPayload: { ...sessionPayload, timestamp: sessionPayload.timestamp ?? new Date().toISOString() },
            id: nanoid(),
          },
        };
      },
    },
    removeSessionPing: (mutableState, action: PayloadAction<SessionId>) => {
      mutableState.sessionPings = mutableState.sessionPings.filter((sp) => sp.id !== action.payload);
    },
    clearOfflineSessionPings: (mutableState) => {
      mutableState.offlineSessionPings = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(UpdateSessionEffects.sendSessionPings.pending, (mutableState) => {
      mutableState.status = StateStatus.LOADING;
    });

    builder.addCase(UpdateSessionEffects.sendSessionPings.fulfilled, (mutableState, action) => {
      const isSessionPingComplete = (sessionPing: SessionPing) =>
        !action.payload.sessions.some((session) => session.id === sessionPing.id);

      mutableState.sessionPings = mutableState.sessionPings.filter(isSessionPingComplete);

      mutableState.status = StateStatus.SUCCESS;
    });
    builder.addCase(UpdateSessionEffects.sendSessionPings.rejected, (mutableState, action) => {
      UtilsSerice.debugLog('UpdateSessionEffects.sendSessionPings.rejected', action.error);
      mutableState.status = StateStatus.FAILED;
      mutableState.error = action.error;
    });
  },
});

export const {
  addSessionPing,
  removeSessionPing,
  addOfflineSessionPing,
  clearOfflineSessionPings,
  addHydratedSessionPing,
} = UpdateSessionState.actions;
export const getUpdateSessions = (state: RootState) => state.updateSessionState.sessionPings;
